import { uuidv7 } from '../domain/ids';
import { getMeta, setMeta } from '../data/appMeta';
import type { SqlDriver } from '../data/db/driver';

/**
 * Auth is local-first: the app is fully usable before (or without) an
 * account. On first launch the device gets a local identity; signing in
 * later (needed only for sync and households) links that identity to a
 * remote account and claims the local data. Nothing in the data layer
 * depends on being signed in.
 */
export interface Session {
  /** Stable id of the person using the app. Local until an account is linked. */
  userId: string;
  kind: 'local' | 'account';
  email?: string;
}

export interface DeviceIdentity {
  /** Unique per install; used as the HLC node id and in sync bookkeeping. */
  deviceId: string;
  session: Session;
}

export interface AuthProvider {
  readonly name: string;
  signIn(): Promise<Session>;
  signOut(): Promise<void>;
}

const DEVICE_ID = 'device.id';
const LOCAL_USER_ID = 'auth.localUserId';

/** After signing in on a second device: from now on this install is the account's person (read at the next start). */
export async function adoptUserId(db: SqlDriver, userId: string): Promise<void> {
  await setMeta(db, LOCAL_USER_ID, userId);
}

export async function loadDeviceIdentity(db: SqlDriver): Promise<DeviceIdentity> {
  let deviceId = await getMeta(db, DEVICE_ID);
  if (!deviceId) {
    deviceId = uuidv7();
    await setMeta(db, DEVICE_ID, deviceId);
  }
  let userId = await getMeta(db, LOCAL_USER_ID);
  if (!userId) {
    userId = uuidv7();
    await setMeta(db, LOCAL_USER_ID, userId);
  }
  return { deviceId, session: { userId, kind: 'local' } };
}
